import { t } from '@lingui/core/macro';
import { Box, Divider, Drawer, IconButton, Link, Stack, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useLocation, useSearch } from '@tanstack/react-router';
import { ChevronLeftIcon } from 'components/icons/ChevronLeftIcon';
import { ChevronRightIcon } from 'components/icons/ChevronRightIcon';
import Logo from 'components/Logo';
import Scrollbar from 'components/Scrollbar';
import { NAVBAR } from 'config';
import useCollapseDrawer from 'hooks/useCollapseDrawer';
import useResponsive from 'hooks/useResponsive';
import { type ReactNode, useEffect } from 'react';
import { useAuthStore } from 'stores/authStore';
import WhiteLabelThemeProvider from 'theme/WhiteLabelThemeProvider';
import type { AppTheme } from 'types/models';
import AccountPopover from './AccountPopover';
import { OptImpersonationBanner } from './OptImpersonationBanner';

type RootStyleProps = { drawerWidth: number };

const RootStyle = styled('div', {
  shouldForwardProp: (prop) => prop !== 'drawerWidth',
})<RootStyleProps>(({ drawerWidth, theme }) => ({
  [theme.breakpoints.up(NAVBAR.MOBILE_BREAKPOINT)]: {
    flexShrink: 0,
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.shorter,
    }),
  },
}));

type MobileNavDrawerProps = {
  isOpenSidebar: boolean;
  onCloseSidebar: VoidFunction;
  children: ReactNode;
};

const MobileNavDrawer = ({ isOpenSidebar, onCloseSidebar, children }: MobileNavDrawerProps) => (
  <Drawer
    open={isOpenSidebar}
    onClose={onCloseSidebar}
    PaperProps={{ sx: { width: NAVBAR.DASHBOARD_WIDTH, bgcolor: 'background.default' } }}
  >
    {children}
  </Drawer>
);

type DesktopNavDrawerProps = {
  drawerWidth: number;
  children: ReactNode;
};

const DesktopNavDrawer = ({ drawerWidth, children }: DesktopNavDrawerProps) => (
  <Drawer
    open
    variant="persistent"
    PaperProps={{
      sx: {
        width: drawerWidth,
        bgcolor: 'background.default',
        overflow: 'hidden',
        transition: (theme) =>
          theme.transitions.create('width', {
            duration: theme.transitions.duration.standard,
          }),
      },
    }}
  >
    {children}
  </Drawer>
);

type Props = {
  isOpenSidebar: boolean;
  onCloseSidebar: VoidFunction;
  children: ReactNode;
  appTheme?: AppTheme;
};

export const NavbarBase = ({ isOpenSidebar, onCloseSidebar, children, appTheme }: Props) => {
  const { isCollapse, onToggleCollapse } = useCollapseDrawer();
  const isMobile = useResponsive('down', NAVBAR.MOBILE_BREAKPOINT);

  const authToken = useAuthStore((state) => state.userToken);
  const { access_token } = useSearch({ strict: false });
  const { pathname } = useLocation();

  // biome-ignore lint/correctness/useExhaustiveDependencies: close sidebar on navigation
  useEffect(() => {
    if (isOpenSidebar) onCloseSidebar();
  }, [pathname]);

  const drawerWidth = isCollapse ? NAVBAR.DASHBOARD_COLLAPSE_WIDTH : NAVBAR.DASHBOARD_WIDTH;

  const header = (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent={isCollapse ? 'center' : 'space-between'}
      sx={{ pt: 3, pb: 2, px: 2.5, flexShrink: 0 }}
    >
      {!isCollapse && <Logo appTheme={appTheme} />}
      {!isMobile && (
        <Tooltip
          title={isCollapse ? t`Ouvrir la navigation` : t`Réduire la navigation`}
          placement="right"
        >
          <IconButton onClick={onToggleCollapse} size="small">
            {isCollapse ? (
              <ChevronRightIcon sx={{ width: 20, height: 20 }} />
            ) : (
              <ChevronLeftIcon sx={{ width: 20, height: 20 }} />
            )}
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );

  const footer = (
    <>
      <OptImpersonationBanner />
      <Divider />
      <Stack sx={{ px: isCollapse ? 1 : 2, py: 2 }}>
        {authToken === null && access_token !== null ? (
          <Link href="/login/email" alignSelf="center">
            {t`Se connecter`}
          </Link>
        ) : (
          <AccountPopover />
        )}
      </Stack>
    </>
  );

  const content = (
    <Box sx={{ height: 1, display: 'flex', flexDirection: 'column' }}>
      {header}

      <Scrollbar sx={{ flexGrow: 1 }}>{children}</Scrollbar>
      <Box sx={{ flexShrink: 0 }}>{footer}</Box>
    </Box>
  );

  return (
    <WhiteLabelThemeProvider appTheme={appTheme}>
      <RootStyle drawerWidth={drawerWidth}>
        <nav>
          {isMobile ? (
            <MobileNavDrawer isOpenSidebar={isOpenSidebar} onCloseSidebar={onCloseSidebar}>
              {content}
            </MobileNavDrawer>
          ) : (
            <DesktopNavDrawer drawerWidth={drawerWidth}>{content}</DesktopNavDrawer>
          )}
        </nav>
      </RootStyle>
    </WhiteLabelThemeProvider>
  );
};
